import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../../context/AuthContext";
import { searchConversation, getMessages, sendMessage } from "../../../api/whatsapp";
import {
  getWhatsAppSocket,
  joinConversation,
  leaveConversation,
} from "../../../services/whatsappSocket";
import type { WhatsAppMessage } from "../../../types/whatsapp";
import { IconClose } from "../../../components/Icons";
import {
  ResizeHandle,
  useResizableDrawer,
} from "../../../hooks/useResizableDrawer";

interface WhatsAppChatProps {
  phone: string;
  customerName: string;
  onClose: () => void;
}

export default function WhatsAppChat({
  phone,
  customerName,
  onClose,
}: WhatsAppChatProps) {
  const { token } = useAuth();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [inputText, setInputText] = useState("");
  const [connected, setConnected] = useState(false);
  const { width, startResize } = useResizableDrawer({ initial: 480, min: 320 });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load conversation and messages
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        const result = await searchConversation(phone, token);
        if (cancelled) return;

        const convId = result.conversation.id;
        setConversationId(convId);

        const messagesResult = await getMessages(convId, token);
        if (cancelled) return;

        setMessages(messagesResult.messages);
      } catch (err: unknown) {
        if (cancelled) return;
        if (err && typeof err === "object" && "status" in err && (err as { status: number }).status === 404) {
          setError("No hay conversacion WhatsApp para este numero");
        } else {
          setError("Error al cargar la conversacion");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [phone, token]);

  // Socket.io real-time
  useEffect(() => {
    if (!conversationId) return;

    const socket = getWhatsAppSocket();
    joinConversation(conversationId);

    const handleConnect = () => setConnected(true);
    const handleDisconnect = () => setConnected(false);

    const handleNewMessage = (msg: WhatsAppMessage) => {
      if (msg.conversationId === conversationId) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
    };

    const handleStatusUpdate = (data: {
      messageId: string;
      deliveryStatus: string;
    }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === data.messageId
            ? { ...m, deliveryStatus: data.deliveryStatus as WhatsAppMessage["deliveryStatus"] }
            : m,
        ),
      );
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("newMessage", handleNewMessage);
    socket.on("messageStatusUpdate", handleStatusUpdate);

    if (socket.connected) setConnected(true);

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("newMessage", handleNewMessage);
      socket.off("messageStatusUpdate", handleStatusUpdate);
      leaveConversation(conversationId);
    };
  }, [conversationId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Send message handler
  const handleSend = useCallback(async () => {
    if (!conversationId || !inputText.trim() || sending) return;

    const text = inputText.trim();
    setInputText("");
    setSending(true);

    try {
      await sendMessage(conversationId, text, token);
    } catch {
      setInputText(text);
    } finally {
      setSending(false);
      textareaRef.current?.focus();
    }
  }, [conversationId, inputText, sending, token]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 100) + "px";
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <aside
        style={{ ...drawerStyle, width, position: "relative" }}
        onClick={(e) => e.stopPropagation()}
      >
        <ResizeHandle onMouseDown={startResize} />
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: ".95rem" }}>
              {customerName}
            </div>
            <div style={{ fontSize: ".75rem", opacity: 0.85 }}>
              {phone}
              {conversationId && (
                <span style={{ marginLeft: "0.5rem" }}>
                  {connected ? "En linea" : "Desconectado"}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={closeButtonStyle}
            title="Cerrar"
          >
            <IconClose />
          </button>
        </div>

        {/* Messages area */}
        <div style={messageListStyle}>
          {loading && (
            <div style={centeredStyle}>Cargando conversacion...</div>
          )}

          {error && <div style={centeredStyle}>{error}</div>}

          {!loading && !error && messages.length === 0 && (
            <div style={centeredStyle}>No hay mensajes</div>
          )}

          {!loading &&
            !error &&
            messages.map((msg) => (
              <MessageBubble key={msg.id} message={msg} />
            ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        {conversationId && !error && (
          <div style={inputAreaStyle}>
            <textarea
              ref={textareaRef}
              value={inputText}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Escribe un mensaje..."
              rows={1}
              disabled={sending}
              style={textareaStyle}
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!inputText.trim() || sending}
              style={{
                ...sendButtonStyle,
                opacity: inputText.trim() && !sending ? 1 : 0.5,
              }}
            >
              <svg
                viewBox="0 0 24 24"
                style={{ width: "1.2rem", height: "1.2rem", fill: "white" }}
              >
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </div>
        )}
      </aside>
    </div>
  );
}

// ─── Message bubble sub-component ────────────────────────────────────────

function MessageBubble({ message }: { message: WhatsAppMessage }) {
  const isIncoming = isPhoneNumber(message.from);
  const time = formatTime(message.timestamp);

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isIncoming ? "flex-start" : "flex-end",
        padding: "1px 0",
      }}
    >
      <div
        style={{
          maxWidth: "80%",
          padding: "6px 10px",
          borderRadius: isIncoming ? "0 8px 8px 8px" : "8px 0 8px 8px",
          background: isIncoming ? "#fff" : "#dcf8c6",
          boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
          fontSize: ".85rem",
          lineHeight: 1.35,
          wordBreak: "break-word",
        }}
      >
        {!isIncoming && (
          <div
            style={{
              fontSize: ".7rem",
              color: "#075e54",
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            {message.from}
          </div>
        )}
        {message.templateName && (
          <div
            style={{
              fontSize: ".65rem",
              color: "#8696a0",
              fontStyle: "italic",
              marginBottom: 2,
            }}
          >
            Template: {message.templateName}
          </div>
        )}
        <span>{message.body}</span>
        <span style={metaStyle}>
          {time}
          {!isIncoming && <StatusTicks status={message.deliveryStatus} />}
        </span>
      </div>
    </div>
  );
}

function StatusTicks({
  status,
}: {
  status: WhatsAppMessage["deliveryStatus"];
}) {
  if (!status) return null;
  const isRead = status === "read";
  const isDouble = status === "delivered" || status === "read";
  return (
    <span style={{ marginLeft: 3, color: isRead ? "#53bdeb" : "#8696a0" }}>
      {isDouble ? "✓✓" : "✓"}
    </span>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function isPhoneNumber(from: string): boolean {
  return /^\+?\d[\d\s-]{6,}$/.test(from);
}

function formatTime(timestamp: string): string {
  try {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

// ─── Styles ──────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  zIndex: 100,
  display: "flex",
  justifyContent: "flex-end",
};

const drawerStyle: React.CSSProperties = {
  width: "min(480px, 90vw)",
  height: "100vh",
  background: "#e5ddd5",
  display: "flex",
  flexDirection: "column",
  boxShadow: "-4px 0 24px rgba(0,0,0,0.18)",
};

const headerStyle: React.CSSProperties = {
  padding: "0.75rem 1rem",
  background: "#075e54",
  color: "white",
  display: "flex",
  alignItems: "center",
  gap: "0.75rem",
  flexShrink: 0,
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "white",
  cursor: "pointer",
  padding: 4,
  display: "flex",
  alignItems: "center",
};

const messageListStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "0.75rem 1rem",
  display: "flex",
  flexDirection: "column",
  gap: "2px",
};

const centeredStyle: React.CSSProperties = {
  textAlign: "center",
  color: "#667781",
  fontSize: ".85rem",
  padding: "2rem 1rem",
};

const metaStyle: React.CSSProperties = {
  fontSize: ".65rem",
  color: "#8696a0",
  marginLeft: 6,
  whiteSpace: "nowrap",
  float: "right",
  marginTop: 4,
};

const inputAreaStyle: React.CSSProperties = {
  padding: "0.5rem 0.75rem",
  background: "#f0f0f0",
  display: "flex",
  gap: "0.5rem",
  alignItems: "flex-end",
  flexShrink: 0,
};

const textareaStyle: React.CSSProperties = {
  flex: 1,
  resize: "none",
  border: "none",
  borderRadius: "8px",
  padding: "8px 12px",
  fontSize: ".85rem",
  lineHeight: 1.4,
  outline: "none",
  fontFamily: "inherit",
  maxHeight: 100,
  overflow: "auto",
};

const sendButtonStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: "50%",
  background: "#075e54",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};
